/* eslint-disable max-len */
import 'react-toastify/dist/ReactToastify.css';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

// import Iframe from 'react-iframe';
import { GUID } from '@babylonjs/core';
import { CartReducer } from '../../../Redux/Reducers/CartReducer';
import { NavigationReducer } from '../../../Redux/Reducers/NavigationReducer';
import { useAppDispatch, useAppSelector } from '../../../Redux/Store/hooks';

import { CustomizationConfiguration } from '../../../common/libraries/modelViewer/Data/CustomizationConfiguration';

import { FilePaths } from '../../../common/libraries/fileSystem/filePaths';
import { SLTXFile } from '../../../common/libraries/fileFormats/sltx/sltxFile';

import { CartItem, CartItemHelper } from '../../Data/CartItem';
import { CustomizationValue } from '../../Data/CustomizationValue';
import { CatalogObjectControllerData } from '../../Data/CatalogObjectControllerData';

import { OrderDetailsComponent } from '../Components/Specific/OrderDetailsComponent';

import { CatalogFileCustomAttributesDefinition, PriceData, PriceDataHelpers } from '../../../common/libraries/fileFormats/catalog/catalog';
import { MeasuresItemData } from '../../../common/libraries/fileFormats/measuresItemData';
import { ItemMeasuresInfo } from '../../../common/libraries/fileFormats/ItemMeasuresInfo';

import { ConfigurationWidgetComponent } from '../../../common/libraries/modelViewer/configurationWidget';
import { TreeOptionSelections } from '../../../common/libraries/modelConfiguration/data/optionsSelection';
import { Config } from '../../../Resources/Config';
import { CustomAttributeData } from '../../../common/libraries/customAttributes/customAttributes';
import { CustomAttributeValue, CustomAttributeValues } from '../../../common/libraries/customAttributes/customAttributeValue';
import { CustomAttributeClassPrefix } from '../../../common/libraries/customAttributes/customAttributesClassPrefix';
import { SLTXUtil } from '../../../common/libraries/fileFormats/sltx/sltxUtil';
import { CatalogTagsTranslation } from '../../../common/libraries/modelViewer/Data/catalogTagsTranslation';

type ObjectViewParameters = {
  collectionName : string,
  priceData : PriceData[] | undefined,
  catalogObjectControllerData : CatalogObjectControllerData,
  measures : MeasuresItemData[],
  customLineAttributes : CatalogFileCustomAttributesDefinition | undefined,
  objectCustomizations : CustomizationConfiguration | undefined,
  objectCustomizationsData : CustomizationValue[],
  changeComponentHandler : (selections : TreeOptionSelections) => void,
  changeCustomAttributesHandler: (attributes: CustomAttributeValues) => void;
  configurations : Config,
  tagsTranslation: CatalogTagsTranslation[] | undefined,
};

export function ObjectView({
  collectionName,
  priceData,
  catalogObjectControllerData,
  measures,
  customLineAttributes,
  objectCustomizations,
  objectCustomizationsData,
  changeComponentHandler,
  changeCustomAttributesHandler,
  configurations,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  tagsTranslation,
} : ObjectViewParameters) : JSX.Element {

  // Grab the dispatch hook from Redux
  const dispatch = useAppDispatch();

  // From the store, get the strings
  const strings = useAppSelector((state) => {
    return state.TextsReducer.strings;
  })!;

  // From the store, get the environment settings
  const environmentSettings = useAppSelector((state) => {
    return state.EnvironmentSettingsReducer;
  });

  const translations = useAppSelector((state) => {
    return state.TextsReducer.translation;
  })!;

  const selectedLanguage = useAppSelector((state) => {
    return state.TextsReducer.language;
  })!;

  // From the store, get the cart
  const cartItems = useAppSelector((state) => {
    return state.CartReducer.cartItems;
  });

  const [configuration, setObjectConfiguration] = useState('');
  const [isOrderDetailsInDisplay, setIsOrderDetailsInDisplay] = useState(false);

  const [sltxFile, setSLTXFile] = useState<SLTXFile | undefined>();

  const { actions: cartActions } = CartReducer;
  const { actions: applicationActions } = NavigationReducer;

  const [newCartItem, setNewCartItem] = useState<CartItem | undefined>(undefined);

  const [canAdd2Cart, setCanAdd2Cart] = useState(false);

  const [globalMacxCartItems, setMaxCartItems] = useState<number>(1);

  const numItemsInCart = cartItems.reduce(
    (result, item) => { return result + CartItemHelper.GetQuantity(item); },
    0,
  );

  useEffect(() => {
    const root = document.documentElement;
    const computedStyle = getComputedStyle(root);

    const maxCartItemsStr = computedStyle.getPropertyValue('--max-cart-items').trim();
    const maxCartItemsNum = parseInt(maxCartItemsStr, 10);

    // eslint-disable-next-line no-restricted-globals
    setMaxCartItems(isNaN(maxCartItemsNum) ? -1 : maxCartItemsNum);
  }, []);

  const getComponentPrice = (groupID : number, optionID: number) : PriceData | undefined => {

    if (!catalogObjectControllerData.catalogModelData.data
      || !catalogObjectControllerData.catalogModelData.data.groups) {
      return undefined;
    }

    const group = catalogObjectControllerData.catalogModelData.data.groups.find((g) => g.id === groupID);
    if (!group) {
      return undefined;
    }

    let selectedFolder;
    let selectedOption;
    for (let i = 0; i < group.optionsFolders.length; i++) {
      for (let n = 0; n < group.optionsFolders[i].options.length; n++) {
        if (group.optionsFolders[i].options[n].id === optionID) {
          selectedFolder = group.optionsFolders[i];
          selectedOption = group.optionsFolders[i].options[n];
          break;
        }
      }

      if (selectedFolder && selectedOption) { break; }
    }

    if (!selectedFolder || !selectedOption) { return undefined; }

    const priceOptionId = `${selectedFolder.name}+${selectedOption.name}`;
    const optionPriceData = priceData ? PriceDataHelpers.getOptionPriceData(
      priceData,
      catalogObjectControllerData.catalogModelData.getName(),
      group.name,
      priceOptionId,
    ) : undefined;

    return optionPriceData;
  };

  const getNewComponentPrice = (groupID : number, optionID: number) : PriceData => {

    const newPriceData : PriceData = ({
      model: '',
      group: '',
      option: '',
      price: 0,
    });
    if (!catalogObjectControllerData.catalogModelData.data
      || !catalogObjectControllerData.catalogModelData.data.groups) {
      return newPriceData;
    }

    const group = catalogObjectControllerData.catalogModelData.data.groups.find((g) => g.id === groupID);
    if (!group) {
      return newPriceData;
    }

    newPriceData.group = group.name;

    let selectedFolder;
    let selectedOption;
    for (let i = 0; i < group.optionsFolders.length; i++) {
      for (let n = 0; n < group.optionsFolders[i].options.length; n++) {
        if (group.optionsFolders[i].options[n].id === optionID) {
          selectedFolder = group.optionsFolders[i];
          selectedOption = group.optionsFolders[i].options[n];
          break;
        }
      }

      if (selectedFolder && selectedOption) { break; }
    }

    if (!selectedFolder || !selectedOption) { return newPriceData; }

    let priceOptionId = `${selectedFolder.name}+${selectedOption.name}`;
    if (selectedFolder.name === 'Root') {
      priceOptionId = selectedOption.name;
    }

    newPriceData.model = catalogObjectControllerData.catalogModelData.getName();
    newPriceData.option = priceOptionId;
    newPriceData.price = 0;

    return newPriceData;
  };

  const addItemToCart = (itemQuantity : number, itemDetails : Array<ItemMeasuresInfo>, itemAttributes : CustomAttributeValue[]) => {

    const modelKey = catalogObjectControllerData.catalogModelData.catalogData?.versionControl.key || '';

    const basePrice = catalogObjectControllerData.catalogModelData.getPrice(priceData);

    const optionsPrice = new Array<PriceData>();
    const variantsComposition = catalogObjectControllerData.getVariantComposition();

    let configurationQueryParam = '';

    let totalOptionPrice = 0.0;
    variantsComposition.forEach((v) => {
      configurationQueryParam += `${v.groupID}-${v.optionID}_`;
      const priceInfo = getComponentPrice(v.groupID, v.optionID);
      if (priceInfo) {
        optionsPrice.push(priceInfo);
        totalOptionPrice += priceInfo.price;
      } else {
        optionsPrice.push(getNewComponentPrice(v.groupID, v.optionID));
      }
    });
    let totalItemCustomAttrPrice = 0.0;
    let totalExtraCustomAttrPrice = 0.0;
    if (customLineAttributes !== undefined) {
      totalItemCustomAttrPrice = CustomAttributeData.calculateAttributesTotalPrice(customLineAttributes.attributes, itemAttributes, CustomAttributeClassPrefix.Extra, undefined, priceData);
      totalExtraCustomAttrPrice = CustomAttributeData.calculateAttributesTotalPrice(customLineAttributes.attributes, itemAttributes, undefined, CustomAttributeClassPrefix.Extra, priceData);
    }

    setObjectConfiguration(configurationQueryParam);

    const fileName = GUID.RandomId();
    const folder = FilePaths.getShardedPath(fileName);
    const pathParts = [folder, fileName];
    const guid = pathParts.join('/');
    const totalPrice = basePrice + totalOptionPrice + totalItemCustomAttrPrice;

    const cartItem = CartItemHelper.Create(
      collectionName,
      catalogObjectControllerData.catalogModelData.getName(),
      catalogObjectControllerData.getVariantComposition(),
      catalogObjectControllerData.lastVariantSelected,
      objectCustomizationsData,
      itemQuantity,
      basePrice,
      totalPrice,
      totalExtraCustomAttrPrice,
      modelKey,
      optionsPrice,
      itemDetails,
      catalogObjectControllerData.customAttributesValues,
      itemAttributes,
      guid,
    );

    setNewCartItem({ ...cartItem });
  };

  const orderTakeScreenshot = (id : string, base64 : string) => {
    // TODO: Add Base64 to cart item
    if (!newCartItem || newCartItem.screenshotId !== id) return;

    CartItemHelper.SetCartScreenshot(newCartItem, base64);
    dispatch(cartActions.addToCart({ cartItem: newCartItem }));

    setNewCartItem(undefined);

    // TODO: Generate a GUID for the screenshot id and add it to the order
    // setViewerURL(`${baseWidgetURL}&screenshot=${screenshotId}&configuration=${configurationQueryParam}`);
    toast.success(strings.PRODUCT_ADDED_TOAST_LABEL, {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: false,
      draggable: true,
      theme: 'light',
    });
  };

  const navigateBack = () => {
    dispatch(applicationActions.restoreCatalogObjectCallerInfo());
  };

  useEffect(() => {
    const modelFilePath = FilePaths.join(
      environmentSettings.catalogInfoPath,
      catalogObjectControllerData.catalogModelData.getFileInfoPath(),
    );

    const newSLTXFile = new SLTXFile();
    newSLTXFile.setFilePath(modelFilePath);
    newSLTXFile.openFile(catalogObjectControllerData.catalogModelData.getName()).then((result) => {
      if (result === false) {
        // eslint-disable-next-line no-console
        console.error(`Failed to load the info.json expected to be in ${modelFilePath}`);
      }
      setSLTXFile(newSLTXFile);
      // if (newSLTXFile.rules) setModelRules(newSLTXFile.rules);
    });

  }, []);

  // Load the model rules
  useEffect(() => {
    if (catalogObjectControllerData.catalogModelData.catalogData !== undefined
      && catalogObjectControllerData.catalogModelData.catalogData.rules !== undefined) {

      let bAllRequiredAttrsSet = true;

      sltxFile?.getRootFileContents().customAttributes?.forEach((att) => {
        if (att.isMandatory) {
          const savedAttribute = catalogObjectControllerData.customAttributesValues.find((satt) => satt.attributeKey === att.key);
          if (!savedAttribute) {
            bAllRequiredAttrsSet = false;
          } else if (savedAttribute?.attributeEnabled
          && (savedAttribute?.attributeKey === undefined
            || savedAttribute?.attributeValue === undefined
            || savedAttribute?.attributeValue === '')) {
            bAllRequiredAttrsSet = false;
          }
        }
      });

      const hasOptionX = catalogObjectControllerData.hasOptionWithName(SLTXUtil.DummyOption);
      setCanAdd2Cart(!hasOptionX && bAllRequiredAttrsSet && ((globalMacxCartItems < 0) || (numItemsInCart < globalMacxCartItems)));
    }
  }, [catalogObjectControllerData, numItemsInCart]);

  return (
    <>
      <div
        className="model-3d-representation-holder"
      >
        {/* Model Display */}
        <div
          style={{
            width: '100%',
            height: '100%',
          }}
        >
          { catalogObjectControllerData
            && catalogObjectControllerData.catalogModelData
            && sltxFile
            && (
              <ConfigurationWidgetComponent
                customAttributesValues={catalogObjectControllerData.customAttributesValues}
                modelData={catalogObjectControllerData.catalogModelData}
                sltxFile={sltxFile}
                configuration={configuration}
                configurationApplyId={0}
                catalogPath={configurations.CATALOG_INFO_PATH}
                resourcesPath={configurations.RESOURCES_PATH}
                tagsTranslation={translations}
                priceData={priceData}
                languageSelected={selectedLanguage}
                screenshotId={newCartItem?.screenshotId}
                setScreenshotHandler={orderTakeScreenshot}
                changeComponentHandler={changeComponentHandler}
                changeCustomAttributesHandler={changeCustomAttributesHandler}
                enableEdit={true}
              />
            )}
        </div>

        {/* Model Details */}
        <div
          style={{
            position: 'absolute',
            right: '0px',
            backgroundColor: '#FFFFFFC0',
          }}
        >
          {catalogObjectControllerData && collectionName && (
            <button
              type="button"
              className="btn btn-secondary rounded"
              disabled={!canAdd2Cart}
              style={{
                position: 'fixed',
                minWidth: '320px',
                width: 'calc(50vw - 30px)',
                height: '50px',
                right: '10px',
                top: 'calc(100vh - 110px)',
                zIndex: 1000,
              }}
              onClick={() => setIsOrderDetailsInDisplay(true)}
            >
              {strings.ADD_TO_CART_OBJECT_LABEL}
            </button>
          )}
        </div>
      </div>
      <div className="object-viewer-info-panel" style={{ left: '15px', marginTop: '15px', position: 'fixed' }}>
        <div>
          <button
            type="button"
            style={{
              border: 'none',
              backgroundColor: 'transparent',
            }}
            onClick={() => navigateBack()}
          >
            {`< ${strings.NAVIGATE_BACK}`}
          </button>
        </div>

        <div style={{ left: '15px' }}>
          <h1 className="colectionHeader" style={{ flexGrow: 1, textAlign: 'left', padding: '0' }}>
            {catalogObjectControllerData.catalogModelData.getName()}
          </h1>
        </div>
      </div>
      {isOrderDetailsInDisplay && (
        <OrderDetailsComponent
          orderDetailsInDisplayHandler={setIsOrderDetailsInDisplay}
          addItemToCartHandler={addItemToCart}
          priceData={priceData}
          objectMeasures={measures}
          customLineAttributes={customLineAttributes}
          objectInfo={catalogObjectControllerData}
          objectCustomizations={objectCustomizations}
          languageSelected={selectedLanguage}
          sltxFile={sltxFile}
        />
      )}
    </>
  );

}
